import { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import usePracticeSet from '@/api/usePracticeSet';
import supabaseClient from '@/api/supabaseClient.ts';
import { useQueryClient } from '@tanstack/react-query';
import Routes from '@/router/Routes';
import AnswerFeedback from '@/components/ui/AnswerFeedback/AnswerFeedback.tsx';
import RetryQuestionButton from '@/components/ui/RetryQuestionButton/RetryQuestionButton.tsx';
import { Database } from '@/_constants/database.types';

type QuestionCategory = Database['public']['Enums']['question_types'];

import 'swiper/css';
import 'swiper/css/navigation';
import ModelAnswerButton from '@/components/ui/ModelAnswerButton/ModelAnswerButton';

// Question Card Component
interface QuestionCardProps {
  questionNumber: number;
  score: number | null;
  maxScore: number;
  onClick?: () => void;
  isGrading?: boolean;
  isSectionB?: boolean;
}

const QuestionCard: FC<QuestionCardProps> = ({ questionNumber, score, maxScore, onClick, isGrading, isSectionB }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full p-6 bg-white rounded-[20px] border-[0.72px] border-[#CCCCCC] border-opacity-50 text-left ${
        onClick ? 'hover:shadow-md transition-shadow' : 'cursor-default'
      }`}
    >
      <div className="flex items-center gap-4">
        <span className="text-lg font-medium flex">
          <span className="text-text-primary">{isSectionB ? 'Section B - ' : `Question ${questionNumber} - `}</span>
          {isGrading ? (
            <span className="text-gray-500 flex items-center gap-2 pl-1">
              <svg
                className="animate-spin h-4 w-4 text-orange-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Grading...
            </span>
          ) : (
            <span className="text-orange-600">
              {score !== null ? score : 0}/{maxScore}
            </span>
          )}
        </span>
        {!isGrading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Click to view feedback</span>
            <svg
              className="w-5 h-5 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        )}
      </div>
    </button>
  );
};

const PracticeSetResultsPage: FC = () => {
  const { practiceSetId } = useParams<{ practiceSetId: string }>();
  const navigate = useNavigate();
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const queryClient = useQueryClient();

  // Fetch practice set with answers
  const { data: practiceSet, isLoading } = usePracticeSet(practiceSetId);

  // Force refetch on mount
  useEffect(() => {
    if (practiceSetId) {
      queryClient.invalidateQueries({ queryKey: ['practice_set', practiceSetId] });
    }
  }, [practiceSetId, queryClient]);

  // Subscribe to Realtime updates for grading progress + polling fallback
  useEffect(() => {
    if (!practiceSetId || !practiceSet) return;

    console.log('Setting up Realtime subscriptions for practice set:', practiceSetId);

    // Create a combined channel for all updates related to this practice set
    const channel = supabaseClient
      .channel(`practice_set_updates:${practiceSetId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'practice_attempts',
          filter: `id=eq.${practiceSetId}`,
        },
        (payload) => {
          console.log('🔔 Practice set updated (Realtime):', payload);
          queryClient.invalidateQueries({ queryKey: ['practice_set', practiceSetId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'practice_answers',
          filter: `practice_attempt_id=eq.${practiceSetId}`,
        },
        (payload) => {
          console.log('🔔 Practice answer updated (Realtime):', payload);
          queryClient.invalidateQueries({ queryKey: ['practice_set', practiceSetId] });
        }
      );

    channel.subscribe((status) => {
      console.log('Practice set updates channel subscription status:', status);
      if (status === 'SUBSCRIBED') {
        console.log('✅ Successfully subscribed to practice set updates');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ Error subscribing to practice set updates');
      }
    });

    // Set up polling fallback when grading is in progress
    // This ensures we catch updates even if Realtime fails
    let pollingInterval: NodeJS.Timeout | null = null;

    if (practiceSet.status === 'in_progress') {
      console.log('🔄 Starting polling fallback for grading updates (every 3 seconds)');
      pollingInterval = setInterval(() => {
        console.log('⏰ Polling for grading updates...');
        queryClient.invalidateQueries({ queryKey: ['practice_set', practiceSetId] });
      }, 3000); // Poll every 3 seconds while grading
    }

    return () => {
      console.log('Cleaning up Realtime subscriptions and polling');
      supabaseClient.removeChannel(channel);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [practiceSetId, practiceSet, queryClient]);

  const handleSlideChange = useCallback(() => {
    if (swiperRef.current) {
      setSelectedQuestionIndex(swiperRef.current.activeIndex);
    }
  }, []);

  const handleBack = useCallback(() => {
    if (selectedQuestionIndex !== null) {
      if (selectedQuestionIndex > 0 && swiperRef.current) {
        swiperRef.current.slidePrev();
      } else {
        setSelectedQuestionIndex(null);
      }
    } else {
      navigate(Routes.PRACTICE_AREA);
    }
  }, [selectedQuestionIndex, navigate]);

  // Group answers for display: Non-Section B answers + Section B as one item
  const displayQuestions = useMemo(() => {
    if (!practiceSet?.practice_answers) return { regular: [], sectionB: { answers: [], answeredQuestion: undefined } };

    const regularAnswers = practiceSet.practice_answers.filter((a) => a.questions?.category !== 'SectionB');
    const sectionBAnswers = practiceSet.practice_answers.filter((a) => a.questions?.category === 'SectionB');

    // Find which Section B answer was graded (could be answered or unanswered with 0 score)
    // Priority: 1) Graded with content, 2) Graded without content (0 score), 3) First one
    const gradedSectionBAnswer = sectionBAnswers.find((a) => a.graded_at) || sectionBAnswers[0]; // Fallback to first if none graded yet

    return {
      regular: regularAnswers,
      sectionB: {
        answers: sectionBAnswers,
        answeredQuestion: gradedSectionBAnswer, // Keep property name for compatibility with UI code
      },
    };
  }, [practiceSet]);

  // Calculate total score and max score
  const { totalScore, maxScore } = useMemo(() => {
    if (!practiceSet) return { totalScore: 0, maxScore: 0 };

    let total = 0;
    let max = 0;

    // Add regular questions scores
    displayQuestions.regular.forEach((answer) => {
      total += answer.ai_score || 0;
      max += answer.questions?.marks || 0;
    });

    // Add Section B score (only the answered question)
    if (displayQuestions.sectionB.answeredQuestion) {
      const sectionBAnswer = displayQuestions.sectionB.answeredQuestion;
      total += sectionBAnswer.ai_score || 0;
      max += sectionBAnswer.questions?.marks || 0;
    }

    return { totalScore: total, maxScore: max };
  }, [practiceSet, displayQuestions]);

  const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  const isGrading = practiceSet?.status === 'in_progress';

  if (isLoading || !practiceSet) {
    return (
      <>
        <PageHeader title="Loading..." showBackButton />
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-500">Loading results...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Summary" showBackButton onBackClick={handleBack} titleAlign="left">
        <div className="flex gap-4 ml-auto"></div>
      </PageHeader>
      <div className="flex justify-start">
        {' '}
        {selectedQuestionIndex === null ? (
          <Button
            size="md"
            className="bg-primary hover:bg-dark-hover-secondary border-none text-white px-8"
            onClick={() => navigate(Routes.PRACTICE_AREA)}
          >
            Practice More Questions
          </Button>
        ) : (
          <Button
            size="md"
            className="bg-primary hover:bg-dark-hover-secondary border-none text-white px-8"
            onClick={() => setSelectedQuestionIndex(null)}
          >
            Summary
          </Button>
        )}
      </div>
      <div className="flex-1 flex flex-col gap-6 overflow-hidden">
        {selectedQuestionIndex === null ? (
          /* Summary View */
          <div className="flex-1 flex flex-col gap-5 p-6 overflow-y-auto">
            {/* Questions List */}
            <div className="space-y-5">
              {/* Regular Questions (non-Section B) */}
              {displayQuestions.regular.map((answer, index) => {
                // Only show "Grading..." if:
                // 1. Answer hasn't been graded yet
                // 2. AND the practice set is still in_progress (not already marked as graded)
                const isQuestionGrading = !answer.graded_at && practiceSet.status === 'in_progress';

                return (
                  <QuestionCard
                    key={answer.id}
                    questionNumber={index + 1}
                    score={answer.ai_score ?? null}
                    maxScore={answer.questions?.marks || 0}
                    onClick={() => setSelectedQuestionIndex(index)}
                    isGrading={isQuestionGrading}
                  />
                );
              })}

              {/* Section B - Single card showing answered question */}
              {displayQuestions.sectionB.answeredQuestion &&
                (() => {
                  const sectionBAnswer = displayQuestions.sectionB.answeredQuestion;
                  const isQuestionGrading = !sectionBAnswer.graded_at && practiceSet.status === 'in_progress';
                  return (
                    <QuestionCard
                      key="section-b"
                      questionNumber={displayQuestions.regular.length + 1}
                      score={sectionBAnswer.ai_score ?? null}
                      maxScore={sectionBAnswer.questions?.marks || 0}
                      onClick={() => setSelectedQuestionIndex(displayQuestions.regular.length)}
                      isSectionB
                      isGrading={isQuestionGrading}
                    />
                  );
                })()}
            </div>

            {/* Final Grade */}
            {isGrading ? (
              <div className="bg-gray-400 text-white rounded-[20px] p-6 border-t-[0.72px] border-b-[0.72px] border-[#CCCCCC] border-opacity-50">
                <h2 className="text-2xl font-bold">Final Grade – Calculating...</h2>
              </div>
            ) : (
              <div className="bg-orange-500 text-white rounded-[20px] p-6 border-t-[0.72px] border-b-[0.72px] border-[#CCCCCC] border-opacity-50">
                <h2 className="text-2xl font-bold">
                  Final Grade – {totalScore}/{maxScore}
                </h2>
              </div>
            )}

            {/* What's Next? Call to Action */}
            {!isGrading && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-[20px] p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">What's Next?</h3>
                    <p className="text-gray-700 mb-3">
                      You've completed this practice set and received your grade. Well done!
                    </p>
                    {percentage < 70 ? (
                      <p className="text-gray-700">
                        Want to improve your score? Continue practicing the same question type to master the skills
                        needed, or try different question types to broaden your understanding.
                      </p>
                    ) : (
                      <p className="text-gray-700">
                        Great job! You scored {percentage}%! You can practice more questions of this type to perfect
                        your skills, or try other question types to continue improving.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Question Detail View with Swiper */
          <Swiper
            modules={[Navigation]}
            spaceBetween={0}
            slidesPerView={1}
            initialSlide={selectedQuestionIndex}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={handleSlideChange}
            allowTouchMove={false}
            className="flex-1 w-full"
          >
            {/* Regular Questions */}
            {displayQuestions.regular.map((answer, index) => {
              const questionData = answer.questions;
              const questionPdfUrl = questionData?.pdf_url
                ? supabaseClient.storage.from('past-papers').getPublicUrl(questionData.pdf_url).data.publicUrl
                : undefined;

              return (
                <SwiperSlide key={answer.id} className="h-full">
                  <div className="flex-1 overflow-y-auto p-6 space-y-6 h-full">
                    {/* Question Header */}
                    <QuestionCard
                      questionNumber={index + 1}
                      score={answer.ai_score || 0}
                      maxScore={questionData?.marks || 0}
                    />

                    <AnswerFeedback
                      answerText={answer.answer_text}
                      aiFeedback={answer.ai_feedback}
                      aiStrengths={answer.ai_strengths}
                      aiImprovements={answer.ai_improvements}
                      aiScore={answer.ai_score || undefined}
                      maxScore={questionData?.marks || 0}
                      gradedAt={answer.graded_at}
                      practiceAnswerId={answer.id}
                      existingModelAnswer={questionData?.model_answer}
                      practiceSetId={practiceSetId}
                      questionContent={questionData?.content}
                      imagePageNumbers={questionData?.image_page_numbers}
                      imagePdfUrl={questionPdfUrl}
                    />
                    <div className="flex gap-8 justify-center">
                      {questionData?.id && (
                        <ModelAnswerButton
                          questionId={questionData.id}
                          existingModelAnswer={questionData?.model_answer}
                          practiceSetId={practiceSetId}
                        />
                      )}
                      {/* Retry Button */}
                      {!isGrading &&
                        answer.graded_at &&
                        questionData?.id &&
                        (questionData?.category || practiceSet.question_category) && (
                          <RetryQuestionButton
                            sourceQuestionId={questionData.id}
                            questionCategory={
                              (questionData?.category || practiceSet.question_category) as QuestionCategory
                            }
                            hasImages={
                              questionData?.category === 'SectionB' ? questionData?.has_image === true : undefined
                            }
                          />
                        )}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}

            {/* Section B - Only the answered question */}
            {displayQuestions.sectionB.answeredQuestion &&
              (() => {
                const sectionBAnswer = displayQuestions.sectionB.answeredQuestion;
                const questionData = sectionBAnswer.questions;

                const hasImages = questionData?.has_image === true;
                const sectionBPdfUrl = questionData?.pdf_url
                  ? supabaseClient.storage.from('past-papers').getPublicUrl(questionData.pdf_url).data.publicUrl
                  : undefined;

                return (
                  <SwiperSlide key="section-b" className="h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 h-full">
                      {/* Question Header */}
                      <QuestionCard
                        questionNumber={displayQuestions.regular.length + 1}
                        score={sectionBAnswer.ai_score || 0}
                        maxScore={questionData?.marks || 0}
                        isSectionB
                      />

                      <AnswerFeedback
                        answerText={sectionBAnswer.answer_text}
                        aiFeedback={sectionBAnswer.ai_feedback}
                        aiStrengths={sectionBAnswer.ai_strengths}
                        aiImprovements={sectionBAnswer.ai_improvements}
                        aiScore={sectionBAnswer.ai_score || undefined}
                        maxScore={questionData?.marks || 0}
                        gradedAt={sectionBAnswer.graded_at}
                        practiceAnswerId={sectionBAnswer.id}
                        questionContent={questionData?.content}
                        imagePageNumbers={questionData?.image_page_numbers}
                        imagePdfUrl={sectionBPdfUrl}
                      />
                      <div className="flex gap-8 justify-center">
                        {questionData?.id && (
                          <ModelAnswerButton
                            questionId={questionData.id}
                            existingModelAnswer={questionData?.model_answer}
                            practiceSetId={practiceSetId}
                          />
                        )}
                        {/* Retry Button */}
                        {!isGrading &&
                          sectionBAnswer.graded_at &&
                          questionData?.id &&
                          (questionData?.category || practiceSet.question_category) && (
                            <RetryQuestionButton
                              sourceQuestionId={questionData.id}
                              questionCategory={
                                (questionData?.category || practiceSet.question_category) as QuestionCategory
                              }
                              hasImages={hasImages}
                            />
                          )}
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })()}
          </Swiper>
        )}
      </div>
    </>
  );
};

export default PracticeSetResultsPage;
